import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { RISK_COLORS, RISK_LABELS } from "@/lib/agents";

const STATUSES = ["open", "investigating", "contained", "resolved"];
const STATUS_COLORS = {
  open: "#EF4444", investigating: "#F97316", contained: "#EAB308", resolved: "#10B981",
};

export default function Incidents() {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ severity: "medium" });

  const load = () => api.get("/incidents").then((r) => setItems(r.data.incidents));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.description) return toast.error("Título y descripción");
    await api.post("/incidents", form);
    toast.success("Incidente registrado");
    setForm({ severity: "medium" });
    setDlg(false);
    load();
  };

  const setStatus = async (i, status) => {
    await api.patch(`/incidents/${i.id}`, { status });
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="incidents-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">INCIDENT RESPONSE</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Incidentes</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Registro y coordinación de incidentes operativos y de seguridad.
          </p>
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--danger)] text-white hover:bg-[var(--danger)]/80" data-testid="inc-add">
              <Plus className="w-4 h-4 mr-2" /> Nuevo incidente
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
            <DialogHeader><DialogTitle className="font-display">Nuevo incidente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-mono">Título</Label>
                <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
                       className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="inc-title" />
              </div>
              <div>
                <Label className="label-mono">Descripción</Label>
                <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                          className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono min-h-[120px]" data-testid="inc-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-mono">Severidad</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="inc-sev"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                      {["low","medium","high","critical"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Agente afectado</Label>
                  <Input value={form.affected_agent || ""} onChange={(e) => setForm({ ...form, affected_agent: e.target.value })}
                         placeholder="opcional" className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="inc-agent" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[var(--danger)] text-white hover:bg-[var(--danger)]/80" data-testid="inc-submit">
                Registrar incidente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] p-16 text-center" data-testid="inc-empty">
          <AlertOctagon className="w-8 h-8 mx-auto text-[var(--good)] mb-3" />
          <div className="font-display font-bold text-lg text-[var(--good)]">Sin incidentes</div>
          <p className="text-sm text-[var(--text-dim)]">Todo en verde. La operación va bien.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`inc-${i.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="rounded-none font-mono text-[10px]" style={{ background: RISK_COLORS[i.severity], color: "#0B0E14" }}>
                      {RISK_LABELS[i.severity] || i.severity}
                    </Badge>
                    <Badge className="rounded-none font-mono text-[10px]" style={{ background: STATUS_COLORS[i.status], color: "#0B0E14" }}>
                      {i.status.toUpperCase()}
                    </Badge>
                    {i.affected_agent && (
                      <span className="font-mono text-[10px] text-[var(--accent)] uppercase">· {i.affected_agent}</span>
                    )}
                  </div>
                  <div className="font-display font-bold text-lg">{i.title}</div>
                  <p className="text-sm text-[var(--text-dim)] leading-relaxed mt-1">{i.description}</p>
                  <div className="label-mono mt-2">
                    {new Date(i.created_at).toLocaleString("es-ES")}
                  </div>
                </div>
                <Select value={i.status} onValueChange={(v) => setStatus(i, v)}>
                  <SelectTrigger className="w-40 rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs" data-testid={`inc-set-${i.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
